import React from "react";
import styled from "styled-components";
import { Icons } from "@/assets/icons";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../translations/translations";
import { getProjectDetail } from "../../translations/projectDetails";

const TestCard = ({
  title,
  description,
  siteLink,
  GhLink,
  path,
  offline,
  workRepo,
  stack,
  translationKey,
  icons,
  cardIcons,
  isNew,
  dateCreated,
  commitCount,
  reportUrl,
}) => {
  const { language } = useLanguage();

  // Get translated description if available
  const key = translationKey || path;
  const translatedDescription = getProjectDetail(language, key, "description");
  const displayDescription = translatedDescription || description;

  // Determine which icons to display on the card
  const iconsToDisplay =
    cardIcons || (icons && icons.length > 0 ? icons.slice(0, 2) : null);

  // Format date as dd/mm/yy
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (e) {
      return null;
    }
  };

  const formattedDate = formatDate(dateCreated);

  // Extract repo owner and name from GitHub URL to build Actions URL
  const getActionsUrl = (ghLink) => {
    if (!ghLink) return null;
    try {
      const match = ghLink.match(/github\.com\/([^\/]+)\/([^\/]+)/);
      if (match) {
        const [, owner, repo] = match;
        return `https://github.com/${owner}/${repo}/actions`;
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const actionsUrl = getActionsUrl(GhLink);
  // Use reportUrl if provided, otherwise fall back to Actions page
  const testReportUrl = reportUrl || actionsUrl;

  return (
    <CardBody>
      {isNew && <NewBadge>{getTranslation(language, "common.new")}</NewBadge>}
      <CardHeader>
        <IconContainer>
          {iconsToDisplay &&
            iconsToDisplay.map((icon, index) => (
              <img key={index} src={icon} alt={`stack-${index}`} />
            ))}
        </IconContainer>
        <CardTitle>
          <h2>{title}</h2>
        </CardTitle>
        <Buttons>
          {GhLink && (
            <a href={GhLink} target="_blank" rel="noreferrer" title="View Repository">
              <img id="icon" src={Icons.github} alt="Github" />
            </a>
          )}
          {testReportUrl && (
            <a
              href={testReportUrl}
              target="_blank"
              rel="noreferrer"
              title={
                reportUrl
                  ? getTranslation(language, "tests.viewReport") || "View Test Report"
                  : getTranslation(language, "tests.viewActions") || "View Test Actions"
              }
            >
              <TestReportIcon>📊</TestReportIcon>
            </a>
          )}
        </Buttons>
      </CardHeader>
      <Link to={`/projects/${path}`} className="link">
        <CardContent>
          <CardDesc>
            <p>{displayDescription}</p>
          </CardDesc>
          {(formattedDate || (commitCount !== undefined && commitCount !== null)) && (
            <CardFooter>
              {formattedDate && <CardDate>{formattedDate}</CardDate>}
              {commitCount !== undefined && commitCount !== null && (
                <CardCommits>{commitCount} commits</CardCommits>
              )}
            </CardFooter>
          )}
        </CardContent>
      </Link>
    </CardBody>
  );
};

export default TestCard;

const CardBody = styled.div`
  border: 2px solid rgba(83, 83, 83, 0.3);
  background: linear-gradient(135deg, rgba(200, 233, 250, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
  width: calc(50% - 12.5px);
  max-width: 600px;
  min-width: 300px;
  height: 100%;
  padding: 25px;
  border-radius: 12px;
  position: relative;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 968px) {
    width: calc(50% - 10px);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }

  .link {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    flex: 1;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(200, 233, 250, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%);
    border-color: rgba(83, 83, 83, 0.5);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(83, 83, 83, 0.2);
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;

  img {
    width: 32px;
    height: 32px;
    object-fit: contain;

    @media (max-width: 768px) {
      width: 28px;
      height: 28px;
    }
  }
`;

const CardTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    color: black;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 1.3rem;
    }

    @media (max-width: 480px) {
      font-size: 1.1rem;
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;

  img {
    width: 28px;
    height: 28px;
    transition: all 0.3s;

    @media (max-width: 768px) {
      width: 24px;
      height: 24px;
    }

    &:hover {
      transform: scale(1.1);
    }
  }
`;

const TestReportIcon = styled.span`
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-block;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardDesc = styled.div`
  flex: 1;
  margin-bottom: 15px;

  p {
    text-align: left;
    font-size: 15px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.8);
    margin: 0;

    @media (max-width: 768px) {
      font-size: 14px;
    }

    @media (max-width: 480px) {
      font-size: 13px;
    }
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  margin-top: auto;
  border-top: 1px solid rgba(83, 83, 83, 0.15);
`;

const CardDate = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const CardCommits = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const NewBadge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 3;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: pulse 2s ease-in-out infinite;

  @media (max-width: 1000px) {
    font-size: 10px;
    padding: 4px 10px;
    top: -10px;
    right: 15px;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
  }
`;
